"use client";

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import AppImage from '@/components/ui/AppImage';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {if (e.isIntersecting) {e.target.classList.add('visible');obs.unobserve(e.target);}}),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

const values = [
{
  num: '01',
  title: 'Radical Honesty',
  desc: 'We tell clients what they need to hear, not what they want to hear. Honest feedback builds better work and longer relationships.'
},
{
  num: '02',
  title: 'Craft Over Speed',
  desc: 'We move with urgency but never sacrifice quality. Every detail matters — because details are what separate good from great.'
},
{
  num: '03',
  title: 'Outcomes Over Outputs',
  desc: 'Deliverables are a means to an end. We measure success by the impact our work has on your business, not by how many slides we made.'
},
{
  num: '04',
  title: 'Continuous Learning',
  desc: 'Our industry moves fast. We invest 10% of our time in R&D, emerging tools, and staying ahead of what\'s next.'
}];


const team = [
{
  name: 'Alexandra Voss',
  role: 'Founder & Strategy Director',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4eb892f-1763300291187.png",
  imgAlt: 'Alexandra Voss, founder, a woman with light brown hair in a professional blazer',
  bio: '15 years leading brand transformations for Fortune 500 companies.'
},
{
  name: 'Marcus Adeyemi',
  role: 'Head of Digital',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1bfef8bd5-1763295388609.png",
  imgAlt: 'Marcus Adeyemi, head of digital, a man in a dark shirt with a confident expression',
  bio: 'Former CTO at two successful SaaS exits. Builds systems that scale.'
},
{
  name: 'Yuki Tanaka',
  role: 'Creative Director',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1285ef3e7-1763295664184.png",
  imgAlt: 'Yuki Tanaka, creative director, a person with short dark hair in a creative studio setting',
  bio: 'Award-winning designer. Ex-Pentagram, ex-Wieden+Kennedy.'
},
{
  name: 'Priya Menon',
  role: 'Growth Lead',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1f290582d-1772705272953.png",
  imgAlt: 'Priya Menon, growth lead, a woman in a professional setting with a warm smile',
  bio: 'Data obsessive. Grew three companies from Series A to IPO.'
},
{
  name: 'Ethan Kowalski',
  role: 'Head of Production',
  img: "https://images.unsplash.com/photo-1701100629534-1eaafe0c5bdf",
  imgAlt: 'Ethan Kowalski, head of production, a man with short hair on a film set',
  bio: 'Director and DoP with credits across Netflix, Apple TV+, and global ad campaigns.'
},
{
  name: 'Amara Diallo',
  role: 'Client Success Director',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_182210649-1772066033351.png",
  imgAlt: 'Amara Diallo, client success director, a woman with natural hair in a bright office',
  bio: 'The reason clients come back. Obsessed with making every engagement exceptional.'
}];


export default function CulturePage() {
  useReveal();

  /* Video state */
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoState, setVideoState] = useState<'idle' | 'loading' | 'playing'>('idle');
  const [videoError, setVideoError] = useState(false);

  const handlePlayVideo = () => {
    if (!videoRef.current) return;
    setVideoState('loading');
    videoRef.current.src = 'https://www.w3schools.com/html/mov_bbb.mp4'; // deferred load
    videoRef.current.load();
    videoRef.current.play().
    then(() => setVideoState('playing')).
    catch(() => {setVideoState('idle');setVideoError(true);});
  };

  return (
    <>
      <Header />

      <main className="bg-background overflow-x-hidden">

        {/* ── MINIMAL HEADER ── */}
        <section
          className="pt-32 pb-14 px-6 md:px-12 border-b border-border"
          style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          
          <div className="mb-8">
            <Breadcrumb crumbs={[{ label: 'Culture' }]} />
          </div>
          <div className="max-w-2xl">
            <span className="section-label mb-4 block">Who We Are</span>
            <h1
              className="fluid-h1 font-display font-light leading-none text-primary mb-4"
              style={{ fontFamily: 'var(--font-display)' }}>
              
              Built by people<br />who care deeply.
            </h1>
            <p className="text-muted text-lg leading-relaxed">
              45 strategists, designers, engineers, and producers — united by a shared obsession with work that actually moves things.
            </p>
          </div>
        </section>

        {/* ── ORIGIN STORY ── */}
        <section className="py-24 px-6 md:px-12" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <span className="section-label mb-4 block">Our Story</span>
              <h2
                className="fluid-h2 font-display font-light text-primary mb-8 leading-none"
                style={{ fontFamily: 'var(--font-display)' }}>
                
                Started with a single question.
              </h2>
              <div className="space-y-5 text-muted leading-relaxed">
                <p>
                  In 2014, founder Alexandra Voss left a senior role at a global consultancy with one question: <em className="text-primary not-italic font-medium">"Why do so many agencies charge premium fees for mediocre outcomes?"</em>
                </p>
                <p>
                  The answer, she found, was a misalignment between what clients needed and what agencies were incentivised to deliver. So she built something different — a studio where every engagement is measured against one metric: did the work move the business?
                </p>
                <p>
                  Ten years and 120+ projects later, that question still drives everything we do.
                </p>
              </div>
            </div>

            <div className="reveal reveal-delay-2 relative">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1b6a3a777-1766573588659.png"
                  alt="BusinessSite team collaborating in a bright open office with large windows and natural light"
                  fill
                  className="object-cover"
                  loading="lazy" />
                
              </div>
              {/* Floating stat */}
              <div className="absolute -bottom-6 -left-6 bg-accent text-white rounded-2xl px-6 py-5 shadow-xl">
                <div
                  className="text-3xl font-display font-semibold mb-1"
                  style={{ fontFamily: 'var(--font-display)' }}>
                  
                  2014
                </div>
                <div className="text-xs text-white/70 font-medium uppercase tracking-wider">Founded</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── VALUES ── */}
        <section
          className="py-24 px-6 md:px-12"
          style={{ background: 'var(--color-secondary)' }}>
          
          <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
            <div className="reveal mb-14">
              <span className="section-label mb-4 block">What We Stand For</span>
              <h2
                className="fluid-h2 font-display font-light text-primary leading-none"
                style={{ fontFamily: 'var(--font-display)' }}>
                
                Our values
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((v, i) =>
              <div
                key={v.num}
                className={`reveal reveal-delay-${i % 2 + 1} bg-background border border-border rounded-2xl p-10 card-lift`}>
                
                  <span className="text-xs font-bold text-accent tracking-widest uppercase mb-4 block">{v.num}</span>
                  <h3
                  className="text-2xl font-display font-light text-primary mb-4"
                  style={{ fontFamily: 'var(--font-display)' }}>
                  
                    {v.title}
                  </h3>
                  <p className="text-muted leading-relaxed">{v.desc}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── TEAM GRID ── */}
        <section className="py-24 px-6 md:px-12" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          <div className="reveal mb-14">
            <span className="section-label mb-4 block">The Team</span>
            <h2
              className="fluid-h2 font-display font-light text-primary leading-none"
              style={{ fontFamily: 'var(--font-display)' }}>
              
              The people behind the work
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {team.map((member, i) =>
            <div
              key={member.name}
              className={`reveal reveal-delay-${i % 3 + 1} group`}>
              
                <div className="relative overflow-hidden rounded-2xl mb-5 aspect-[4/5]">
                  <AppImage
                  src={member.img}
                  alt={member.imgAlt}
                  fill
                  className="object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                  loading="lazy" />
                
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-5 left-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-sm leading-relaxed">{member.bio}</p>
                  </div>
                </div>
                <h3
                className="text-xl font-display font-light text-primary mb-1"
                style={{ fontFamily: 'var(--font-display)' }}>
                
                  {member.name}
                </h3>
                <p className="text-sm text-muted font-medium">{member.role}</p>
              </div>
            )}
          </div>
        </section>

        {/* ── VIDEO SECTION ── */}
        <section
          className="py-24 px-6 md:px-12 relative overflow-hidden"
          style={{ background: 'var(--color-primary)' }}>
          
          <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
            <div className="reveal text-center mb-12">
              <span className="section-label mb-4 block" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Behind the Scenes
              </span>
              <h2
                className="fluid-h2 font-display font-light text-white leading-none"
                style={{ fontFamily: 'var(--font-display)' }}>
                
                See how we work
              </h2>
            </div>

            {/* Video container */}
            <div className="reveal relative rounded-2xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
              {/* Poster / Idle state */}
              {videoState === 'idle' &&
              <>
                  <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_13b768c6a-1772766262371.png"
                  alt="Team working in a creative studio environment — click to watch our behind-the-scenes video"
                  fill
                  className="object-cover"
                  loading="lazy" />
                
                  <div className="absolute inset-0 bg-primary/50 flex flex-col items-center justify-center">
                    <button
                    onClick={handlePlayVideo}
                    className="group flex flex-col items-center gap-4"
                    aria-label="Play behind-the-scenes video">
                    
                      <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center group-hover:scale-110 group-hover:bg-accent transition-all duration-300">
                        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="ml-1">
                          <path d="M8 5.5v17l17-8.5L8 5.5z" fill="white" />
                        </svg>
                      </div>
                      <span className="text-white/80 text-sm font-medium">Play Video · 2:30</span>
                    </button>
                  </div>
                </>
              }

              {/* Loading spinner */}
              {videoState === 'loading' &&
              <div className="absolute inset-0 bg-primary flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full border-2 border-white/20 border-t-accent spinner" />
                </div>
              }

              {/* Error state */}
              {videoError &&
              <div className="absolute inset-0 bg-primary flex flex-col items-center justify-center gap-4">
                  <p className="text-white/60 text-sm">Video unavailable. Please try again.</p>
                  <button
                  onClick={() => {setVideoError(false);setVideoState('idle');}}
                  className="btn-ghost text-sm">
                  
                    Retry
                  </button>
                </div>
              }

              {/* Video element — deferred src */}
              <video
                ref={videoRef}
                className={`absolute inset-0 w-full h-full object-cover ${videoState === 'playing' ? 'block' : 'hidden'}`}
                controls
                playsInline
                onEnded={() => setVideoState('idle')}
                aria-label="Behind-the-scenes video of BusinessSite team at work" />
              
            </div>
          </div>
        </section>

        {/* ── BRIDGE CTA ── */}
        <section className="py-20 px-6 md:px-12 text-center reveal">
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <p className="text-muted text-lg mb-6 leading-relaxed">
              Great work comes from great collaboration. See what we can build together.
            </p>
            <Link href="/services" className="btn-outline">
              Explore Our Services
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </Link>
          </div>
        </section>

      </main>

      <Footer />
    </>);

}